import { DraftSelectorModal } from '@/features/check-in/components';
import { useDrafts } from '@/features/check-in/hooks/use-drafts';
import { useUnreadCount } from '@/features/notifications/hooks/use-notifications';
import { useAppGate } from '@/features/setup/gate/use-app-gate';
import { useResponsiveBreakpoint } from '@/hooks/use-responsive-breakpoint';
import { router } from 'expo-router';
import React, { createContext, useCallback, useContext, useState } from 'react';

export type ResponsiveShellContextType = {
  isDesktop: boolean;
  isLargeDesktop: boolean;
  /**
   * Whether the app chrome (sidebar, top bar, drawer, global modals) may paint.
   * True only on the app tier of the gate -- see the provider for why this is
   * not the same thing as "the user is authenticated".
   */
  isAppShellVisible: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  onCheckIn: () => void;
  unreadCount: number | undefined;
};

const ResponsiveShellContext = createContext<ResponsiveShellContextType | null>(null);

export function useResponsiveShell() {
  const context = useContext(ResponsiveShellContext);
  if (!context) {
    throw new Error('useResponsiveShell must be used within a ResponsiveShellProvider');
  }
  return context;
}

// Optional hook that doesn't throw - for components that may be outside the provider
export function useResponsiveShellOptional() {
  return useContext(ResponsiveShellContext);
}

interface ResponsiveShellProviderProps {
  children: React.ReactNode;
}

export function ResponsiveShellProvider({ children }: ResponsiveShellProviderProps) {
  const breakpoint = useResponsiveBreakpoint();
  const isDesktop = breakpoint === 'desktop' || breakpoint === 'large-desktop';
  const isLargeDesktop = breakpoint === 'large-desktop';
  // Keyed on the app gate, not on auth status (ADR 0006). `verified` is true the
  // instant a social signup lands, while the `(setup)` group still owns the
  // screen -- painting the shell then is exactly the "app shell frames the gate"
  // failure the route group exists to eliminate. `gate === 'app'` rather than
  // `!needsSetup` because `needsSetup` is false during boot as well, when
  // nothing has been decided yet.
  const { gate } = useAppGate();
  const isAppShellVisible = gate === 'app';
  const { data: unreadCount } = useUnreadCount();
  const { drafts, deleteDraft, refreshDrafts } = useDrafts();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const onCheckIn = useCallback(async () => {
    const currentDrafts = await refreshDrafts();
    if (currentDrafts.length > 0) {
      setShowDraftModal(true);
    } else {
      router.push('/(root)/check-in/quick');
    }
  }, [refreshDrafts]);

  // Handle starting a new check-in from modal
  const handleStartNew = useCallback(() => {
    setShowDraftModal(false);
    router.push('/(root)/check-in/quick');
  }, []);

  // Handle selecting a draft from modal
  const handleSelectDraft = useCallback((draftId: string) => {
    setShowDraftModal(false);
    router.push({
      pathname: '/(root)/check-in/quick',
      params: { draftId },
    });
  }, []);

  // Handle deleting a draft from modal
  const handleDeleteDraft = useCallback(async (draftId: string) => {
    try {
      await deleteDraft(draftId);
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  }, [deleteDraft]);

  const value: ResponsiveShellContextType = {
    isDesktop,
    isLargeDesktop,
    isAppShellVisible,
    drawerOpen,
    openDrawer,
    closeDrawer,
    onCheckIn,
    unreadCount,
  };

  return (
    <ResponsiveShellContext.Provider value={value}>
      {children}
      {/* Draft selector modal is global and available once the app is open */}
      {isAppShellVisible && (
        <DraftSelectorModal
          visible={showDraftModal}
          onClose={() => setShowDraftModal(false)}
          drafts={drafts}
          onSelectDraft={handleSelectDraft}
          onStartNew={handleStartNew}
          onDeleteDraft={handleDeleteDraft}
        />
      )}
    </ResponsiveShellContext.Provider>
  );
}
